import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Clock, Plus, X } from 'lucide-react';

const DAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' }
];

const TIME_SLOTS = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'
];

const AvailabilitySelector = ({ availability = {}, onChange, compact = false }) => {
  const [selectedDay, setSelectedDay] = useState('monday');
  const [startTime, setStartTime] = useState('4:00 PM');
  const [endTime, setEndTime] = useState('8:00 PM');

  // Convert time string to index for comparison
  const timeToIndex = (time) => TIME_SLOTS.indexOf(time);

  // Generate time slots between start and end
  const generateTimeRange = (start, end) => {
    const startIdx = timeToIndex(start);
    const endIdx = timeToIndex(end);
    if (startIdx === -1 || endIdx === -1 || startIdx >= endIdx) return [];
    return TIME_SLOTS.slice(startIdx, endIdx + 1);
  };

  // Add time range to a day
  const handleAddTimeRange = () => {
    const slots = generateTimeRange(startTime, endTime);
    if (slots.length === 0) {
      return;
    }

    const currentSlots = availability[selectedDay] || [];
    const newSlots = [...new Set([...currentSlots, ...slots])].sort(
      (a, b) => timeToIndex(a) - timeToIndex(b)
    );

    onChange({
      ...availability,
      [selectedDay]: newSlots
    });
  };

  // Remove a specific time slot from a day
  const handleRemoveSlot = (day, slot) => {
    const currentSlots = availability[day] || [];
    const newSlots = currentSlots.filter(s => s !== slot);
    
    const newAvailability = { ...availability };
    if (newSlots.length === 0) {
      delete newAvailability[day];
    } else {
      newAvailability[day] = newSlots;
    }
    onChange(newAvailability);
  };

  // Toggle entire day
  const handleToggleDay = (day, checked) => {
    const newAvailability = { ...availability };
    if (checked) {
      // Add default time slots (4 PM - 8 PM)
      newAvailability[day] = ['4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'];
    } else {
      delete newAvailability[day];
    }
    onChange(newAvailability);
  };

  // Clear all availability for a day
  const handleClearDay = (day) => {
    const newAvailability = { ...availability };
    delete newAvailability[day];
    onChange(newAvailability);
  };

  // Format time range for display
  const formatTimeRange = (slots) => {
    if (!slots || slots.length === 0) return 'Not available';
    if (slots.length === 1) return slots[0];
    
    // Find consecutive ranges
    const ranges = [];
    let rangeStart = slots[0];
    let lastSlot = slots[0];
    
    for (let i = 1; i < slots.length; i++) {
      const currentIdx = timeToIndex(slots[i]);
      const lastIdx = timeToIndex(lastSlot);
      
      if (currentIdx !== lastIdx + 1) {
        ranges.push(rangeStart === lastSlot ? rangeStart : `${rangeStart} - ${lastSlot}`);
        rangeStart = slots[i];
      }
      lastSlot = slots[i];
    }
    ranges.push(rangeStart === lastSlot ? rangeStart : `${rangeStart} - ${lastSlot}`);
    
    return ranges.join(', ');
  };

  if (compact) {
    // Compact view for display only
    return (
      <div className="space-y-2">
        {DAYS.map(day => {
          const slots = availability[day.key] || [];
          if (slots.length === 0) return null;
          return (
            <div key={day.key} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-slate-700 w-20">{day.short}:</span>
              <span className="text-slate-600">{formatTimeRange(slots)}</span>
            </div>
          );
        })}
        {Object.keys(availability).length === 0 && (
          <p className="text-sm text-slate-500 italic">No availability set</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Clock className="h-4 w-4" />
        Weekly Availability
      </Label>

      {/* Quick day toggles */}
      <div className="flex flex-wrap gap-2">
        {DAYS.map(day => (
          <div key={day.key} className="flex items-center gap-2">
            <Checkbox
              id={`day-${day.key}`}
              checked={!!availability[day.key]?.length}
              onCheckedChange={(checked) => handleToggleDay(day.key, checked)}
            />
            <Label 
              htmlFor={`day-${day.key}`} 
              className="text-sm cursor-pointer"
            >
              {day.short}
            </Label>
          </div>
        ))}
      </div>

      {/* Add custom time range */}
      <div className="p-4 bg-stone-50 rounded-xl space-y-3">
        <p className="text-sm font-medium text-slate-700">Add Time Range</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Day</Label>
            <Select value={selectedDay} onValueChange={setSelectedDay}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS.map(day => (
                  <SelectItem key={day.key} value={day.key}>{day.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">From</Label>
            <Select value={startTime} onValueChange={setStartTime}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOTS.map(time => (
                  <SelectItem key={time} value={time}>{time}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">To</Label>
            <Select value={endTime} onValueChange={setEndTime}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_SLOTS.filter(t => timeToIndex(t) > timeToIndex(startTime)).map(time => (
                  <SelectItem key={time} value={time}>{time}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            type="button"
            size="sm"
            onClick={handleAddTimeRange}
            className="rounded-full"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Current availability display */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-700">Current Schedule</p>
        {DAYS.map(day => {
          const slots = availability[day.key] || [];
          if (slots.length === 0) return null;
          
          return (
            <div key={day.key} className="flex items-center justify-between p-3 bg-white border border-stone-200 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="font-medium text-slate-900 w-24">{day.label}</span>
                <span className="text-sm text-slate-600">{formatTimeRange(slots)}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleClearDay(day.key)}
                className="text-slate-400 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        {Object.keys(availability).filter(k => availability[k]?.length > 0).length === 0 && (
          <p className="text-sm text-slate-500 italic p-3 bg-stone-50 rounded-lg">
            No availability set. Use the toggles above or add custom time ranges.
          </p>
        )}
      </div>
    </div>
  );
};

export default AvailabilitySelector;
